import { graphql, DataValue } from '@apollo/client/react/hoc';
import * as React from 'react';
import cx from 'classnames';

import CurrentUser from 'auth/CurrentUser';
import FormElement from 'components/FormElement';
import Glyph from 'components/Glyph';
import Group from 'entities/Group';
import LayerTrigger from 'components/LayerTrigger';
import Link from 'components/Link';
import Menu from 'components/Menu';
import MenuItem from 'components/MenuItem';
import Select from 'components/SelectDeprecated';

import { LoadUserOrganizationsForGroupCreateOrEdit_me_organizations as UserOrganization } from 'organizations/queries/__generated__/LoadUserOrganizationsForGroupCreateOrEdit';

import { AssociatedOrganizationForGroupEditForm_me_organizations as AssociatedOrganization } from 'groups/queries/__generated__/AssociatedOrganizationForGroupEditForm';

import './styles/GroupSchoolSelect.scss';

export const UNAFFILIATED_CLASS_ID = '-1';
export const unaffiliatedOrgOption = {
  value: UNAFFILIATED_CLASS_ID,
  label: 'Not affiliated with a school',
};
export type OrgOption = {
  value: string;
  label: string;
};

export type Props = {
  onSchoolChange?: ((selectedOrgOption?: UserOrganization) => void) | null;
  group?: Group | null;
  hideUnaffiliated?: boolean | null;
  hasChildren?: boolean | null;
  filterUnadministered?: boolean | null;
  prefix?: string | null;
  input?: string | null;
  initialOrganization: OrgOption;
  orgOptions: UserOrganization[] | AssociatedOrganization[];
};

type State = {
  selectedOrg: OrgOption | undefined | null;
};

class GroupSchoolSelect extends React.Component<Props, State> {
  static defaultProps = {
    input: 'select',
    hideUnaffiliated: false,
    filterUnadministered: false,
  };

  constructor(props: Props) {
    super(props);

    this.state = {
      selectedOrg: props.initialOrganization || unaffiliatedOrgOption,
    };
  }

  componentDidMount() {
    if (this.state.selectedOrg) {
      this.handleSchoolChange(this.state.selectedOrg.value);
    }
  }

  componentWillReceiveProps(nextProps: Props) {
    if (this.props.hideUnaffiliated !== nextProps.hideUnaffiliated) {
      const schoolId = this.getSelectedSchoolId();
      if (schoolId) {
        this.handleSchoolChange(schoolId);
      }
    }
  }

  unaffiliatedIsInvalid(): boolean {
    return this.props.hideUnaffiliated || (!!this.props.group && this.props.group.hasChildren());
  }

  handleSchoolChange = (orgIdString: string) => {
    const { onSchoolChange, orgOptions } = this.props;
    const selectedOrgOption = orgOptions.find((o) => o.id === parseInt(orgIdString));
    if (selectedOrgOption || orgIdString === UNAFFILIATED_CLASS_ID) {
      this.setState({
        selectedOrg:
          orgIdString === UNAFFILIATED_CLASS_ID
            ? unaffiliatedOrgOption
            : // @ts-ignore - possibly undefined warning
              { value: selectedOrgOption.id.toString(), label: selectedOrgOption.name },
      });
    }

    if (onSchoolChange) {
      const orgId = orgIdString || UNAFFILIATED_CLASS_ID; // this can be UNAFFILIATED_CLASS_ID (-1)
      const matchingOrg = orgOptions && orgOptions.find((org) => org.id.toString() === orgId);
      onSchoolChange(matchingOrg);
    }
  };

  getSchoolSelectOptions = () => {
    const organizations = this.props.orgOptions;
    if (!organizations) {
      return [];
    }

    const validOrgs = organizations.reduce((arr, o) => {
      if (
        o.type !== 'District' &&
        (!this.props.filterUnadministered || (o.admins && o.admins.length > 0))
      ) {
        arr.push(o);
      }
      return arr;
    }, [] as typeof organizations);

    const organizationOptions = validOrgs.map<OrgOption>((org: UserOrganization) => {
      return { value: org.id.toString(), label: org.name };
    });

    if (!this.props.hasChildren) {
      organizationOptions.push(unaffiliatedOrgOption);
    }
    return organizationOptions;
  };

  getSelectedSchoolName(): string {
    const { selectedOrg } = this.state;
    return selectedOrg ? selectedOrg.label : 'Not affiliated with a school';
  }

  getSelectedSchoolId(): string | undefined | null {
    const { selectedOrg } = this.state;
    const defaultValue = UNAFFILIATED_CLASS_ID;
    return selectedOrg ? selectedOrg.value : defaultValue;
  }

  getOrganizationForSubmit(): OrgOption | undefined | null {
    return this.state.selectedOrg;
  }

  shouldShowSchool(): boolean {
    if (CurrentUser.isSignedIn()) {
      return CurrentUser.getUser().isOrganizationPickerEligible();
    }
    return false;
  }

  preExistingUnaffiliatedU13(): boolean {
    return this.state && !this.state.selectedOrg && this.unaffiliatedIsInvalid();
  }

  renderMenu() {
    // TODO: rm un-namspaced classes
    const schoolId = this.getSelectedSchoolId();

    const menu = (
      <Menu
        className={cx('GroupSchoolSelect GroupSchoolSelect--menu', {
          'GroupSchoolSelect--unaffiliated': !this.unaffiliatedIsInvalid(),
          'GroupForm-schoolMenu': true,
          'GroupForm-hasUnaffiliated': !this.unaffiliatedIsInvalid(),
        })}
        selectedKey={schoolId ? schoolId.toString() : ''}
      >
        {this.getSchoolSelectOptions().map((option: OrgOption) => (
          <MenuItem key={option.value} onSelect={this.handleSchoolChange}>
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    );

    return (
      <div>
        {this.state.selectedOrg && this.props.prefix}
        <LayerTrigger layer={menu} trigger="click" placement="bottom" alignment="middle">
          <Link href="#">
            <span>
              {this.getSelectedSchoolName()}
              <Glyph name="chevron-down" size="xsmall" />
            </span>
          </Link>
        </LayerTrigger>
      </div>
    );
  }

  renderSelect() {
    return (
      <div className="remove-select-clearable-cross-hack">
        <FormElement
          label="School"
          ref="school"
          // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
          id="schoolSelect"
          onValidChange={(value) => this.handleSchoolChange(value)}
          element={Select}
          elementProps={{
            name: 'school',
            placeholder: 'Select a school',
            searchable: false,
            clearable: false, // clearable DOESN'T WORK!, the little cross still aprears
            options: this.getSchoolSelectOptions(),
            value: this.getSelectedSchoolId(),
            className: cx('GroupSchoolSelect GroupSchoolSelect--select', {
              'GroupSchoolSelect--unaffiliated': !this.unaffiliatedIsInvalid(),
              'GroupForm-schoolSelect': true,
              'GroupForm-hasUnaffiliated': !this.unaffiliatedIsInvalid(),
            }),
          }}
        />
      </div>
    );
  }

  render() {
    if (!this.shouldShowSchool()) {
      return null;
    }

    return this.props.input === 'menu' ? this.renderMenu() : this.renderSelect();
  }
}

export { GroupSchoolSelect };

export default GroupSchoolSelect;
